import {type DefaultError, useMutation, useQueryClient} from "@tanstack/react-query";
import {queryHttp} from "~/query/queryHttp";
import {notificationStore} from "~/stores/init";
import {
    supplierEnrichmentProfileUrl,
    supplierEntityDetailUrl,
    supplierEntityLogoUrl,
} from "~/pages/supplier-360/services/supplier360Urls";
import {
    supplierSuggestionDecisionUrl,
    supplierSuggestionsUrl,
} from "~/pages/supplier-360/services/supplier360PdlUrls";
import type {SupplierSuggestion} from "~/pages/supplier-360/types/Supplier360PdlTypes";

export type DecideSuggestionVariables = {
    suggestion: SupplierSuggestion;
    decision: 'accept' | 'dismiss';
};

/**
 * Accept or dismiss one suggestion. Accepting a `domain` suggestion is written server-side exactly
 * like a user's own domain edit (a manual lock, a logo refresh), so those caches are refreshed too —
 * accepting an `alias` only records the decision (no canonical alias field exists to write into yet).
 */
export function useDecideSuggestionMutation() {
    const queryClient = useQueryClient();

    return useMutation<SupplierSuggestion, DefaultError, DecideSuggestionVariables>({
        mutationFn: async ({suggestion, decision}) =>
            (await queryHttp.post<SupplierSuggestion>(supplierSuggestionDecisionUrl(suggestion.id, decision))).data,
        onSuccess: (decided, {suggestion, decision}) => {
            const entityId = suggestion.entity;
            // Dismissed rows leave the list; accepted ones stay (an accepted alias is shown from here).
            queryClient.setQueryData<SupplierSuggestion[]>(
                [supplierSuggestionsUrl(entityId)],
                previous => previous
                    ?.map(s => s.id === decided.id ? decided : s)
                    .filter(s => s.state !== 'dismissed'),
            );
            if (decision === 'accept' && suggestion.field === 'domain') {
                void queryClient.invalidateQueries({queryKey: [supplierEnrichmentProfileUrl(entityId)]});
                void queryClient.invalidateQueries({queryKey: [supplierEntityDetailUrl(entityId)]});
                // `logo_url` also lives on the registry list cache, same as a manual domain edit
                // (useUpdateSupplierEnrichmentMutation) — without this the registry keeps showing the
                // stale logo until an unrelated refetch happens to occur.
                void queryClient.invalidateQueries({
                    predicate: (query) => {
                        const key = query.queryKey[0];
                        return typeof key === 'string' && key.startsWith('supplier_360/entity/?');
                    },
                });
                void queryClient.resetQueries({queryKey: [supplierEntityLogoUrl(entityId)]});
            }
        },
        onError: (_error, {suggestion}) => {
            notificationStore.show('Could not save that decision. Please try again.', {
                severity: 'error',
                id: `supplier-suggestion-error-${suggestion.id}`,
                autoHideDuration: 3000,
            });
        },
    });
}
